/**
 * O protocolo do caso como card colapsado (E78.F3.T06).
 *
 * Antes, o formulário de oito seções ficava sempre aberto — e, no caso real,
 * repetido em cada uma das cinco peças. Agora o que fica na tela é o resumo do
 * que a plataforma declarou, em quatro colunas, mais o que ela não informou; o
 * formulário só reaparece quando o escritório clica em "Editar protocolo", e é
 * o MESMO `ProtocoloForm` — sem segunda tela de transcrição a manter.
 *
 * As duas verificações moram aqui, e não dentro do formulário, porque precisam
 * estar alcançáveis com ele fechado. Elas continuam travadas enquanto há
 * alteração não salva: verificar ali rodaria o motor sobre o protocolo
 * anterior. "Atualizar fontes externas" segue separada e discreta — é a única
 * ação que gasta consulta paga, e o aviso de custo fica visível, não em dica.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ProtocoloAssinaturaCasoData, ProtocoloAssinaturaData } from '@/lib/exame_eletronico';
import type { ProtocoloImportadoData } from '@/lib/exame_eletronico_importacao';
import { resumoDoProtocolo, type ResumoDoProtocolo } from '@/lib/exame_eletronico_resumo';

import { ImportarProtocoloCard } from './ImportarProtocoloCard';
import { ProtocoloForm, type ProtocoloFormAcao } from './ProtocoloForm';

const t = ptBR.exameEletronico;
const tr = ptBR.exameEletronico.resumoProtocolo;
const tf = ptBR.exameEletronico.form;
const tp = ptBR.exameEletronico.tabelaDePecas;

function Colunas({ resumo }: { resumo: ResumoDoProtocolo }): JSX.Element {
  return (
    <div
      className="grid gap-sc-4 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="protocolo-resumo-colunas"
    >
      {resumo.colunas.map((coluna) => (
        <div key={coluna.chave} data-testid={`protocolo-resumo-${coluna.chave}`}>
          <h3 className="font-display text-[13px] uppercase tracking-[0.06em] text-fg-muted">
            {coluna.rotulo}
          </h3>
          <ul className="mt-sc-1 space-y-sc-1 text-body-sm text-fg">
            {coluna.linhas.map((linha) => (
              <li key={linha}>{linha}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Ausencias({ ausencias }: { ausencias: readonly string[] }): JSX.Element {
  return (
    <p className="text-body-sm text-fg-muted" data-testid="protocolo-resumo-ausencias">
      <span className="text-fg-dim">{tr.ausenciasTitulo}:</span>{' '}
      {ausencias.length === 0 ? tr.ausenciasVazio : ausencias.join(' · ')}
    </p>
  );
}

/** Rótulo do botão: gira quando a ação correspondente está em voo. */
function Rotulo({ girando, texto }: { girando: boolean; texto: string }): JSX.Element {
  if (!girando) return <>{texto}</>;
  return (
    <>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {texto}
    </>
  );
}

function Cabecalho({ resumo }: { resumo: ResumoDoProtocolo | null }): JSX.Element {
  return (
    <header>
      <h2 id="protocolo-do-caso-titulo" className="text-display-sm text-fg">
        {t.protocoloDoCasoTitulo}
      </h2>
      {resumo === null ? (
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{tr.semProtocolo}</p>
      ) : (
        <>
          <p className="mt-1 text-body-md text-fg" data-testid="protocolo-resumo-cabecalho">
            {resumo.cabecalho}
          </p>
          <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{tr.intro}</p>
        </>
      )}
    </header>
  );
}

interface AcoesProps {
  temResumo: boolean;
  editando: boolean;
  emVoo: boolean;
  concluido: boolean;
  podeVerificar: boolean;
  rotuloVerificar: string;
  acaoEmCurso: ProtocoloFormAcao | null;
  onAlternarEdicao: () => void;
  onVerificar: () => void;
  onAtualizarConsultas: () => void;
}

function Acoes({
  temResumo,
  editando,
  emVoo,
  concluido,
  podeVerificar,
  rotuloVerificar,
  acaoEmCurso,
  onAlternarEdicao,
  onVerificar,
  onAtualizarConsultas,
}: AcoesProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-sc-3 border-t border-border pt-sc-4">
      {temResumo ? (
        <Button
          type="button"
          variant="secondary"
          onClick={onAlternarEdicao}
          disabled={emVoo || concluido}
          data-testid="protocolo-editar"
        >
          {editando ? tr.fecharEdicao : tr.editar}
        </Button>
      ) : null}
      <Button
        type="button"
        disabled={!podeVerificar}
        onClick={onVerificar}
        data-testid="protocolo-verificar"
      >
        <Rotulo
          girando={acaoEmCurso === 'verificando'}
          texto={acaoEmCurso === 'verificando' ? tf.verificando : rotuloVerificar}
        />
      </Button>
      <Button
        type="button"
        variant="ghost"
        disabled={!podeVerificar}
        onClick={onAtualizarConsultas}
        data-testid="protocolo-atualizar-consultas"
      >
        <Rotulo
          girando={acaoEmCurso === 'atualizando'}
          texto={acaoEmCurso === 'atualizando' ? tf.atualizandoConsultas : tf.atualizarConsultas}
        />
      </Button>
    </div>
  );
}

const ti = ptBR.exameEletronico.importarProtocolo;

/** O aviso que fica visível enquanto a sugestão não foi confirmada (§2.2/§2.3). */
function AvisoDeSugestao({ importado }: { importado: ProtocoloImportadoData }): JSX.Element {
  const assistidos = Object.values(importado.sugestao.proveniencia).filter(
    (origem) => origem === 'assistida',
  ).length;
  return (
    <div
      className="bg-surface-raised border border-border-strong p-sc-3"
      data-testid="protocolo-sugestao-aviso"
      role="status"
    >
      <p className="text-body-sm text-fg">{ti.marcaSugerido}</p>
      <p className="mt-1 max-w-prose text-caption text-fg-dim">{ti.sugestaoAviso}</p>
      {assistidos > 0 && (
        <p
          className="mt-1 max-w-prose text-caption text-fg-dim"
          data-testid="protocolo-sugestao-assistida"
        >
          {ti.marcaAssistida}
        </p>
      )}
    </div>
  );
}

/**
 * A sugestão importada e o que ela faz ao card (E77.F1).
 *
 * Fica num hook porque são três coisas que só fazem sentido juntas: guardar a
 * proposta, abrir o formulário para conferência, e **soltá-la quando o
 * protocolo salvo chega** — a partir daí o que está na tela é dado gravado, e
 * continuar chamando-o de sugestão mentiria para o perito.
 */
function useSugestaoImportada(protocoloSalvo: ProtocoloAssinaturaCasoData | null) {
  const [importado, setImportado] = useState<ProtocoloImportadoData | null>(null);
  const [visto, setVisto] = useState(protocoloSalvo);
  const mudou = visto !== protocoloSalvo;
  if (mudou) {
    setVisto(protocoloSalvo);
    setImportado(null);
  }
  return { importado, setImportado, protocoloMudou: mudou };
}

interface CorpoProps {
  editando: boolean;
  resumo: ResumoDoProtocolo | null;
  paraEditar: ProtocoloAssinaturaCasoData | null;
  acaoEmCurso: ProtocoloFormAcao | null;
  concluido: boolean;
  onAlterado: () => void;
  onSalvar: (protocolo: ProtocoloAssinaturaData) => void;
  onVerificar: () => void;
  onAtualizarConsultas: () => void;
}

/** O miolo do card: o formulário aberto, ou o resumo colapsado. */
function CorpoDoCard({
  editando,
  resumo,
  paraEditar,
  acaoEmCurso,
  concluido,
  onAlterado,
  onSalvar,
  onVerificar,
  onAtualizarConsultas,
}: CorpoProps): JSX.Element | null {
  if (editando) {
    return (
      <ProtocoloForm
        // Remonta ao recarregar E ao chegar sugestão nova: o estado inicial
        // passa a ser o que se propõe, sobre o que já estava gravado.
        key={JSON.stringify(paraEditar)}
        idPrefix="protocolo-caso"
        protocoloInicial={paraEditar}
        acaoEmCurso={acaoEmCurso}
        concluido={concluido}
        ocultarCodigoDeclarado
        mostrarAcoesDeVerificacao={false}
        onAlterado={onAlterado}
        onSalvar={onSalvar}
        onVerificar={onVerificar}
        onAtualizarConsultas={onAtualizarConsultas}
      />
    );
  }
  if (resumo === null) return null;
  return (
    <>
      <Colunas resumo={resumo} />
      <Ausencias ausencias={resumo.ausencias} />
    </>
  );
}

interface ImportacaoProps {
  caseId: string;
  concluido: boolean;
  importado: ProtocoloImportadoData | null;
  onSugestao: (importado: ProtocoloImportadoData) => void;
}

/** A importação e o aviso de que o que está na tela ainda é proposta. */
function BlocoDeImportacao({
  caseId,
  concluido,
  importado,
  onSugestao,
}: ImportacaoProps): JSX.Element | null {
  // Com peça concluída nada mais se altera (ADR-0077 §2.4): oferecer a
  // importação seria oferecer um caminho que o servidor recusaria.
  if (concluido) return null;
  return (
    <>
      <ImportarProtocoloCard caseId={caseId} concluido={concluido} onSugestao={onSugestao} />
      {importado !== null && <AvisoDeSugestao importado={importado} />}
    </>
  );
}

export interface ProtocoloDoCasoCardProps {
  caseId: string;
  protocolo: ProtocoloAssinaturaCasoData | null;
  /** Quantas peças o "Verificar" vai rodar de uma vez. */
  totalDePecas: number;
  acaoEmCurso: ProtocoloFormAcao | null;
  /** O perito concluiu alguma peça: nada mais pode ser alterado (ADR-0077 §2.4). */
  concluido: boolean;
  onSalvar: (protocolo: ProtocoloAssinaturaData) => void;
  onVerificar: () => void;
  onAtualizarConsultas: () => void;
}

export function ProtocoloDoCasoCard({
  caseId,
  protocolo,
  totalDePecas,
  acaoEmCurso,
  concluido,
  onSalvar,
  onVerificar,
  onAtualizarConsultas,
}: ProtocoloDoCasoCardProps): JSX.Element {
  const resumo = resumoDoProtocolo(protocolo);
  // Sem protocolo transcrito não há resumo a colapsar: a tela abre no formulário.
  const [editando, setEditando] = useState(resumo === null);
  const [alterado, setAlterado] = useState(false);
  const { importado, setImportado, protocoloMudou } = useSugestaoImportada(protocolo);

  // Salvar recarrega o caso e traz um protocolo novo: a marca de "alterado"
  // cai junto e o card volta ao resumo, sem efeito nem callback de sucesso.
  if (protocoloMudou) {
    setAlterado(false);
    setEditando(protocolo === null);
  }

  // O que o formulário edita: a proposta, quando há; o gravado, quando não.
  const paraEditar = importado?.sugestao ?? protocolo;

  const emVoo = acaoEmCurso !== null;
  // Sugestão não confirmada conta como alteração pendente: é o mesmo mecanismo
  // que já travava o Verificar, e é o que garante que o motor nunca rode sobre
  // um campo que o perito não salvou (ADR-0078 §2.2).
  const precisaSalvar = resumo === null || alterado || importado !== null;
  const podeVerificar = !precisaSalvar && !emVoo && !concluido;
  const rotuloVerificar =
    totalDePecas === 1
      ? tp.verificarUmaPeca
      : tp.verificarPecas.replace('{n}', String(totalDePecas));

  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="protocolo-do-caso"
      data-editando={editando}
      aria-labelledby="protocolo-do-caso-titulo"
    >
      <Cabecalho resumo={resumo} />

      <BlocoDeImportacao
        caseId={caseId}
        concluido={concluido}
        importado={importado}
        onSugestao={(resposta) => {
          setImportado(resposta);
          // A sugestão só serve se estiver à vista para conferência.
          setEditando(true);
        }}
      />

      <CorpoDoCard
        editando={editando}
        resumo={resumo}
        paraEditar={paraEditar}
        acaoEmCurso={acaoEmCurso}
        concluido={concluido}
        onAlterado={() => setAlterado(true)}
        onSalvar={onSalvar}
        onVerificar={onVerificar}
        onAtualizarConsultas={onAtualizarConsultas}
      />

      <Acoes
        temResumo={resumo !== null}
        editando={editando}
        emVoo={emVoo}
        concluido={concluido}
        podeVerificar={podeVerificar}
        rotuloVerificar={rotuloVerificar}
        acaoEmCurso={acaoEmCurso}
        onAlternarEdicao={() => setEditando((atual) => !atual)}
        onVerificar={onVerificar}
        onAtualizarConsultas={onAtualizarConsultas}
      />
      {precisaSalvar && !concluido ? (
        <p className="text-caption text-fg-dim" data-testid="protocolo-verificar-hint">
          {tf.verificarHint}
        </p>
      ) : null}
      <p className="max-w-prose text-caption text-fg-dim" data-testid="protocolo-aviso-custo">
        {tf.atualizarConsultasHint}
      </p>
    </Blueprint>
  );
}
